import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import { useNavigate } from 'react-router-dom';

import AddRounded from '@mui/icons-material/AddRounded';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { AuthContext } from 'authentication/AuthContext';
import { formatNumberWithoutDecimals } from 'utils/helpers/format';
import { GT_ZERO, REQUIRED } from 'utils/helpers/validationMessages';
import ParentShipmentSummary from './ParentShipmentSummary';
import RestrictedLocations from './RestrictedLocations';
import DistributionWizardLayout from './DistributionWizardLayout';
import { isOptionEqualToValue } from '../../utils';
import { baseDistribution } from './initialState';

import {
  useGetInventoryOwnersQuery,
  useGetLocationsQuery,
  useGetRecipientsQuery,
} from '../../batchesEndpoints';

const DistributionDetails = ({
  availableVolumeExceeded,
  distributionsState,
  initialValidationState,
  isFetchingShipment,
  onClose,
  onNext,
  originId,
  productId,
  setDistributionsState,
  setValidationMessages,
  shipmentData,
  totalProposedVolume,
  validationMessages,
}) => {
  const navigate = useNavigate();
  const { distributions } = distributionsState;
  const { affiliates, currentAffiliate } = useContext(AuthContext);
  const dataCompanies = affiliates.reduce((a, c) => {
    return [c.dataCompanyId, ...a];
  }, []);
  const dataCompanyId = R.path(['shipper', 'dataCompanyId'], shipmentData);

  if (!dataCompanies.includes(dataCompanyId)) {
    navigate(`/affiliate/${currentAffiliate.customerIdName}/${currentAffiliate.dataCompanyId}`);
  }

  const inventoryOwnersParams = { dataCompanyId };

  const {
    data: inventoryOwnersData = [],
    isFetching: isFetchingInventoryOwners,
    isSuccess: isSuccessInventoryOwners,
  } = useGetInventoryOwnersQuery({ params: inventoryOwnersParams });

  const handleInventoryOwnerChange = (index, value) => {
    setDistributionsState(R.assocPath(['distributions', index, 'inventoryOwner'], value));
    setValidationMessages(
      R.update(index, R.assoc('inventoryOwner', '', validationMessages[index]))
    );
  };

  const recipientsParams = { dataCompanyId };

  const {
    data: recipientsData = [],
    isFetching: isFetchingRecipients,
    isSuccess: isSuccessRecipients,
  } = useGetRecipientsQuery({ params: recipientsParams });

  const validateChainRecipients = R.cond([
    [R.isEmpty(R.__), R.always(REQUIRED)],
    [R.T, R.always('')],
  ]);

  const handleChainRecipientsChange = (index, value) => {
    setDistributionsState(R.assocPath(['distributions', index, 'chainRecipients'], value));
    setValidationMessages(
      R.update(
        index,
        R.assoc('chainRecipients', validateChainRecipients(value), validationMessages[index])
      )
    );
  };

  const locationsParams = { bookingOriginId: originId, dataCompanyId, productId };

  const {
    data: locationsData = [],
    isFetching: isFetchingLocations,
    isSuccess: isSuccessLocations,
  } = useGetLocationsQuery({ params: locationsParams });

  const handleLocationChange = (index, value) => {
    setDistributionsState(R.assocPath(['distributions', index, 'locationId'], value));
    setValidationMessages(R.update(index, R.assoc('locationId', '', validationMessages[index])));
  };

  const validateVolume = R.cond([
    [R.isEmpty(R.__), R.always(REQUIRED)],
    [R.gt(1), R.always(GT_ZERO)],
    [R.T, R.always('')],
  ]);

  const handleVolumeChange = (index, value) => {
    const sanitized = R.replace(/[^0-9]/g, '', value);
    setDistributionsState(
      R.assocPath(['distributions', index, 'volume'], parseFloat(sanitized.replaceAll(',', '')))
    );
    setDistributionsState(
      R.assocPath(['distributions', index, 'volumeDisplay'], formatNumberWithoutDecimals(sanitized))
    );
    setValidationMessages(
      R.update(index, R.assoc('volume', validateVolume(value), validationMessages[index]))
    );
  };

  const handleShipmentChainChange = (index, evt) => {
    const { checked } = evt.target;

    setDistributionsState(R.assocPath(['distributions', index, 'shipmentChain'], checked));

    // reset chainRecipients when shipment chain required is toggled off
    if (R.not(checked)) {
      setDistributionsState(R.assocPath(['distributions', index, 'chainRecipients'], []));
      setValidationMessages(
        R.update(index, R.assoc('chainRecipients', '', validationMessages[index]))
      );
    }
  };

  const handleReconsignmentControlChange = (index, evt) => {
    const { checked } = evt.target;

    setDistributionsState(R.assocPath(['distributions', index, 'reconsignmentControl'], checked));
  };

  const handleAddDistributionClick = () => {
    setDistributionsState(R.assoc('distributions', R.append(baseDistribution, distributions)));
    setValidationMessages(R.append(initialValidationState));
  };

  const renderTags = (tags, getTagProps) =>
    tags.map((item, index) => (
      <Chip
        label={item.recipientName}
        sx={{ borderColor: 'secondary.main', borderRadius: 25 }}
        variant="outlined"
        {...getTagProps({ index })}
      />
    ));

  const isInvalid = [
    availableVolumeExceeded,
    R.map(
      R.compose(R.anyPass([R.isNil, R.lte(R.__, 0), R.equals(NaN)]), R.prop('volume')),
      distributions
    ),
    R.map(R.compose(R.isNil, R.prop('inventoryOwner')), distributions),
    R.map(R.compose(R.isNil, R.prop('locationId')), distributions),
    R.map(
      R.ifElse(
        R.propEq('shipmentChain', true),
        R.compose(R.isEmpty, R.prop('chainRecipients')),
        R.F
      ),
      distributions
    ),
  ]
    .flat()
    .includes(true);

  const buttons = (
    <>
      <Button onClick={onClose} sx={{ width: 104 }} variant="outlined">
        Back
      </Button>
      <Button disabled={isInvalid} onClick={onNext} sx={{ width: 104 }}>
        Review
      </Button>
    </>
  );

  return (
    <DistributionWizardLayout
      buttons={buttons}
      onClose={onClose}
      pageTitle="Add New Distribution"
      parentDetails={
        isFetchingShipment ? (
          <Box textAlign="center" width="100%">
            <CircularProgress size={48} />
          </Box>
        ) : (
          <ParentShipmentSummary
            shipmentData={shipmentData}
            totalProposedVolume={totalProposedVolume}
          />
        )
      }
    >
      <Card
        elevation={1}
        sx={{
          pb: 11,
          pt: { md: 3, xs: 2 },
          px: { md: 3, xs: 2 },
        }}
      >
        <CardHeader
          subheader="Please provide the following details for your distribution(s)."
          sx={{ padding: 0 }}
          title="Distributions"
        />
        {distributions.map(
          (
            {
              inventoryOwner,
              locationId,
              shipmentChain,
              chainRecipients,
              reconsignmentControl,
              volumeDisplay,
            },
            index
          ) => {
            const count = R.add(index, 1);

            return (
              // eslint-disable-next-line react/no-array-index-key
              <CardContent key={index} sx={{ padding: 0 }}>
                <Stack spacing={2} sx={{ py: { md: 3, xs: 2 } }}>
                  <Typography variant="subtitle1">Distribution {count}</Typography>
                  <Autocomplete
                    disableClearable
                    disabled={!isSuccessInventoryOwners}
                    getOptionLabel={R.prop('inventoryOwnerName')}
                    isOptionEqualToValue={isOptionEqualToValue('inventoryOwnerIdName')}
                    loading={isFetchingInventoryOwners}
                    onChange={(_, value) => handleInventoryOwnerChange(index, value)}
                    options={inventoryOwnersData}
                    renderInput={inputParams => (
                      <TextField
                        {...inputParams}
                        error={R.not(R.isEmpty(validationMessages[index].inventoryOwner))}
                        helperText={validationMessages[index].inventoryOwner}
                        label="Inventory Owner"
                      />
                    )}
                    value={inventoryOwner}
                  />

                  <Stack spacing={1}>
                    <Autocomplete
                      disableClearable
                      disabled={R.or(!isSuccessLocations, R.isEmpty(locationsData))}
                      getOptionLabel={R.prop('locationName')}
                      isOptionEqualToValue={isOptionEqualToValue('id')}
                      loading={isFetchingLocations}
                      onChange={(_, value) => handleLocationChange(index, value)}
                      options={locationsData}
                      renderInput={inputParams => (
                        <TextField
                          {...inputParams}
                          error={R.not(R.isEmpty(validationMessages[index].locationId))}
                          helperText={validationMessages[index].locationId}
                          label="Location"
                        />
                      )}
                      value={locationId}
                    />
                    {R.and(isSuccessLocations, R.isEmpty(locationsData)) && (
                      <Alert severity="warning">
                        There are no valid locations available for selection. Please contact the
                        Batch Owner to update the origin or product on the parent batch.
                      </Alert>
                    )}
                  </Stack>

                  <TextField
                    error={R.or(
                      !R.isEmpty(validationMessages[index].volume),
                      availableVolumeExceeded
                    )}
                    fullWidth
                    helperText={
                      availableVolumeExceeded
                        ? 'Distribution Volume causes the total distributed volume to exceed the Shipment Volume'
                        : validationMessages[index].volume
                    }
                    label="Volume"
                    onChange={event => handleVolumeChange(index, event.target.value)}
                    slotProps={{
                      input: {
                        endAdornment: <InputAdornment position="end">bbl</InputAdornment>,
                      },
                    }}
                    value={volumeDisplay}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={shipmentChain}
                        color="secondary"
                        onChange={evt => handleShipmentChainChange(index, evt)}
                      />
                    }
                    label={<Typography>Is a shipment chain required?</Typography>}
                  />
                  {shipmentChain && (
                    <Autocomplete
                      disableClearable
                      disabled={!isSuccessRecipients}
                      getOptionLabel={R.prop('recipientName')}
                      isOptionEqualToValue={isOptionEqualToValue('recipientIdName')}
                      loading={isFetchingRecipients}
                      multiple
                      onChange={(_, value) => handleChainRecipientsChange(index, value)}
                      options={recipientsData}
                      renderInput={inputParams => (
                        <TextField
                          {...inputParams}
                          error={R.not(R.isEmpty(validationMessages[index].chainRecipients))}
                          helperText={validationMessages[index].chainRecipients}
                          label="Recipients"
                        />
                      )}
                      renderTags={renderTags}
                      value={chainRecipients}
                    />
                  )}
                  <FormControlLabel
                    control={
                      <Switch
                        checked={reconsignmentControl}
                        color="secondary"
                        disabled={R.or(R.isNil(inventoryOwner), R.isNil(locationId))}
                        onChange={evt => handleReconsignmentControlChange(index, evt)}
                      />
                    }
                    label={
                      <Stack alignItems="center" direction="row" spacing={2}>
                        <Typography>Set a reconsignment control</Typography>
                        <Tooltip title="A Reconsignment Control maximizes efficiencies by limiting loading of transferred barrels to the selected terminal location or a location further from the origin.">
                          <InfoRoundedIcon sx={{ color: 'graphic.main' }} />
                        </Tooltip>
                      </Stack>
                    }
                  />
                  {reconsignmentControl && (
                    <RestrictedLocations
                      inventoryOwner={inventoryOwner}
                      locationId={locationId}
                      originId={originId}
                      productId={productId}
                    />
                  )}
                </Stack>
                {count < distributions.length && <Divider />}
                <Divider />
              </CardContent>
            );
          }
        )}
        <Button
          onClick={handleAddDistributionClick}
          startIcon={<AddRounded sx={{ color: 'secondary.main', mt: -0.5 }} />}
          variant="text"
        >
          Add Another Inventory Owner
        </Button>
      </Card>
    </DistributionWizardLayout>
  );
};

DistributionDetails.propTypes = {
  availableVolumeExceeded: PropTypes.bool.isRequired,
  distributionsState: PropTypes.shape({
    distributions: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  initialValidationState: PropTypes.shape({}).isRequired,
  isFetchingShipment: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  originId: PropTypes.number.isRequired,
  productId: PropTypes.number.isRequired,
  setDistributionsState: PropTypes.func.isRequired,
  setValidationMessages: PropTypes.func.isRequired,
  shipmentData: PropTypes.shape({}).isRequired,
  totalProposedVolume: PropTypes.number.isRequired,
  validationMessages: PropTypes.arrayOf(
    PropTypes.shape({
      chainRecipients: PropTypes.string,
      inventoryOwner: PropTypes.string,
      locationId: PropTypes.string,
      volume: PropTypes.string,
    })
  ).isRequired,
};

export default DistributionDetails;
